/**
 * Pipeline Monitor Component
 *
 * Main container component for the AISecOps pipeline visualization.
 * Combines the track, detail panels, and status cards.
 */

import { useState } from 'react';
import { Activity, Clock, Hash } from 'lucide-react';
import { PipelineTrack } from './PipelineTrack';
import { ClassifyStageDetail } from './ClassifyStageDetail';
import { SecurityStatusCard } from './SecurityStatusCard';
import { ReviewPanel } from './ReviewPanel';
import type { PipelineState, PipelineStage } from '../../types/pipeline';
import { STAGE_LABELS, STAGE_DESCRIPTIONS } from '../../types/pipeline';
import type { NoteApiResponse } from '../../types/api';
import { cn } from '../../lib/utils';

interface PipelineMonitorProps {
  pipelineState: PipelineState;
  noteStatus?: string;
  /** Full note data for review panel */
  note?: NoteApiResponse;
  /** Callback when review action completes */
  onReviewComplete?: () => void;
  /** Compact mode for dashboard widget embedding */
  compact?: boolean;
  className?: string;
}

export function PipelineMonitor({
  pipelineState,
  noteStatus,
  note,
  onReviewComplete,
  compact = false,
  className,
}: PipelineMonitorProps) {
  const [selectedStage, setSelectedStage] = useState<PipelineStage>('CLASSIFY');

  const { stages, classify, noteId } = pipelineState;

  return (
    <div className={cn("widget flex flex-col", className)}>
      {/* Header */}
      <div className="bg-linear-to-r from-bg-secondary to-bg-surface px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-accent-green" />
            <h2 className="text-sm font-semibold text-text-primary">
              AISecOps Processing Pipeline
            </h2>
          </div>
          <div className="flex items-center gap-3 text-xs text-text-muted">
            {noteId && (
              <div className="flex items-center gap-1">
                <Hash className="w-3 h-3" />
                <span className="font-mono">{noteId.slice(0, 8)}...</span>
              </div>
            )}
            {noteStatus && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span className="uppercase font-medium">{noteStatus}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pipeline Track */}
      <div className="border-b border-border bg-bg-primary py-3">
        <PipelineTrack
          stages={stages}
          selectedStage={selectedStage}
          onStageSelect={setSelectedStage}
        />
      </div>

      {/* Stage Detail Area */}
      {!compact && (
        <div className="p-4 space-y-4 flex-1">
          {/* Selected Stage Info */}
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span className="font-medium text-text-primary">
              Selected: {STAGE_LABELS[selectedStage]}
            </span>
            <span className="text-border-light">/</span>
            <span>{STAGE_DESCRIPTIONS[selectedStage]}</span>
          </div>

          {/* CLASSIFY Stage Detail */}
          {selectedStage === 'CLASSIFY' && (
            <ClassifyStageDetail data={classify} />
          )}

          {/* Security Status Card */}
          {(selectedStage === 'PII_SCAN' ||
            selectedStage === 'GUARDRAILS') && (
            <SecurityStatusCard
              piiStatus={stages.PII_SCAN}
              guardrailsStatus={stages.GUARDRAILS}
            />
          )}

          {/* REVIEW Stage — show ReviewPanel */}
          {selectedStage === 'REVIEW' &&
            note &&
            note.status === 'REVIEW' &&
            onReviewComplete && (
              <ReviewPanel
                note={note}
                onReviewComplete={onReviewComplete}
              />
            )}

          {/* Other stages placeholder */}
          {!['CLASSIFY', 'PII_SCAN', 'GUARDRAILS', 'REVIEW'].includes(
            selectedStage
          ) && (
            <div className="bg-bg-primary border border-border rounded-lg p-3 text-xs text-text-muted">
              <p>
                <strong className="text-text-primary">
                  {STAGE_LABELS[selectedStage]}
                </strong>
                : {STAGE_DESCRIPTIONS[selectedStage]}
              </p>
              <p className="mt-1.5 text-text-muted">
                Status:{' '}
                <span className="uppercase font-mono">
                  {stages[selectedStage]}
                </span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
